import React, { ReactElement, useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { OptionType } from 'b2b-components/theme/components/Form/SearchableSelect';
import { useProductsAnalytics } from 'b2b-apps/core/analytics/context/products/ProductsAnalyticsContext';
import { useCartWishlistSearchEffect, useFetchCartWishlistSearchAction } from 'b2b-apps/shop/cart/hooks';
import { SEARCHABLE_NEW_OPTION_VALUE } from 'b2b-apps/shop/common/components/Form/FormFieldSearchableSelect';
import {
  useAddToWishlistAction,
  useClearWishlistAction,
  useClearWishlistsAction,
  useCreateAndAddToWishlistAction,
} from 'b2b-apps/shop/myAccount/wishlists/hooks';
import { Form } from '../Form';

const SEARCHABLE_SELECT_DEBOUNCE = 300;

interface Props {
  skus: string[];
  onModalClose: () => void;
}

export const WithContainer = ({ skus, onModalClose }: Props): ReactElement => {
  const fetchWishlistsSearchAction = useFetchCartWishlistSearchAction();
  const wishlists = useCartWishlistSearchEffect();
  const addToWishlistAction = useAddToWishlistAction();
  const createAndAddToWishlistAction = useCreateAndAddToWishlistAction();
  const clearWishlistsAction = useClearWishlistsAction();
  const clearWishlistAction = useClearWishlistAction();
  const { analyzeProductAddToWishlist } = useProductsAnalytics();
  const onAddToWishlistComplete = (): void => {
    if (skus.length === 1) {
      analyzeProductAddToWishlist(skus[0]);
    }
  };

  const options: OptionType[] = wishlists?.data?.map((wishlist) => ({
    value: wishlist.id,
    label: wishlist.name,
  })) || [];

  useEffect(() => {
    const callback = searchableCallback.current;

    if (callback) {
      callback(options);
    }
  }, [options]);


  const defaultWishlist = options?.[0] || null;

  const searchableCallback = useRef<((options: OptionType[]) => void) | null>(null);
  const [currentWishlist, setCurrentWishlist] = useState<OptionType>();

  const fetchWishlistsDebounced = debounce((searchPhrase: string) => {
    fetchWishlistsSearchAction(searchPhrase);
  }, SEARCHABLE_SELECT_DEBOUNCE);

  const handleLoadOptions = (searchPhrase: string, callback: (...args: any[]) => any): void => {
    if (searchPhrase) {
      fetchWishlistsDebounced(searchPhrase);
      searchableCallback.current = callback;
    }
  };

  const handleChange = (fieldName: string, option: OptionType): void => {
    setCurrentWishlist(option);
  };

  const handleSubmit = (): void => {
    if ((currentWishlist?.value || defaultWishlist) && skus?.length) {
      if (currentWishlist?.value === SEARCHABLE_NEW_OPTION_VALUE) {
        createAndAddToWishlistAction((currentWishlist?.label as string) || '',
          skus,
          (isFulfilled) => {
            if (isFulfilled) {
              onModalClose();
            }
          });
      } else {
        addToWishlistAction(currentWishlist?.value || defaultWishlist?.value || '',
          skus,
          (isFulfilled) => {
            if (isFulfilled) {
              onModalClose();
              clearWishlistAction();
            }
          });
      }
      onAddToWishlistComplete();
      clearWishlistsAction();
    }
  };

  return (
    <Form
      currentWishlist={currentWishlist?.value || defaultWishlist?.value || ''}
      onChange={handleChange}
      onModalClose={onModalClose}
      options={options}
      onLoadOptions={handleLoadOptions}
      onSubmit={handleSubmit}
      wishlists={wishlists}
      itemCount={skus.length}
    />
  );
};
